import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export type CmsLocale = "de" | "en" | "es" | "ar";

/** Subscribe to draft overrides pushed via window.postMessage from the CMS editor. */
export function useCmsDraftOverrides(): Record<string, string> {
  const [v, setV] = useState<Record<string, string>>(() =>
    typeof window !== "undefined" ? (window as any).__cmsDraftOverrides ?? {} : {}
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setV({ ...((window as any).__cmsDraftOverrides ?? {}) });
    window.addEventListener("cms-preview-update", handler);
    return () => window.removeEventListener("cms-preview-update", handler);
  }, []);
  return v;
}

/**
 * Read-only CMS text for public pages.
 * Uses a plain REST fetch so the Supabase SDK stays out of the landing payload.
 */
const REST_URL = import.meta.env.VITE_SUPABASE_URL as string;
const REST_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

async function cmsFetch(path: string) {
  const res = await fetch(`${REST_URL}/rest/v1/${path}`, {
    headers: { apikey: REST_KEY, Authorization: `Bearer ${REST_KEY}` },
  });
  if (!res.ok) throw new Error("cms fetch failed");
  return res.json();
}

export function useCmsText(page: string) {
  const { i18n } = useTranslation();
  const locale = ((i18n.language || "de").split("-")[0]) as CmsLocale;
  const overrides = useCmsDraftOverrides();

  const { data } = useQuery({
    queryKey: ["cms", "page", page],
    queryFn: async () => {
      const contents: any[] = await cmsFetch(
        `cms_content?select=*&page=eq.${encodeURIComponent(page)}&order=section,sort_order`
      );
      const ids = contents.map((c: any) => c.id);
      const translations: any[] = ids.length
        ? await cmsFetch(`cms_translations?select=*&content_id=in.(${ids.join(",")})`)
        : [];
      return { contents, translations };
    },
  });

  const map = useMemo(() => {
    const m = new Map<string, string>();
    if (!data) return m;
    const byId = new Map(data.contents.map((c: any) => [c.id, c.key]));
    for (const t of data.translations) {
      if (t.locale !== locale) continue;
      const key = byId.get(t.content_id) as string | undefined;
      if (key && t.value && t.value.trim().length > 0) m.set(key, t.value);
    }
    return m;
  }, [data, locale]);

  return (key: string, fallback?: string) => {
    const full = key.startsWith(`${page}.`) ? key : `${page}.${key}`;
    const ovKey = `${full}::${locale}`;
    if (overrides && ovKey in overrides) return overrides[ovKey];
    return map.get(full) ?? fallback ?? "";
  };
}
